import { markHelperInstalledOnThisDevice, wasHelperInstalledOnThisDevice } from '@/config/helperPresence';
import {
  fetchHelperHealth,
  resolveHelperConnectionState,
} from '@/features/serial/helperClient';
import { useArduinoSetupDialogStore } from '@/state/arduinoSetupDialogStore';
import { useHelperDialogStore } from '@/state/helperDialogStore';
import { useHelperStartDialogStore } from '@/state/helperStartDialogStore';
import { useSerialStore } from '@/state/serialStore';
import { useEffect } from 'react';

function promptForOfflineHelper(): void {
  if (wasHelperInstalledOnThisDevice()) {
    useHelperStartDialogStore.getState().showStartupPromptOnce();
    return;
  }
  useHelperDialogStore.getState().showStartupPromptOnce();
}

/** On page load, detect helper status and prompt to start or download as needed. */
export function useHelperStartupPrompt(): void {
  useEffect(() => {
    if (import.meta.env.DEV) {
      return;
    }

    let cancelled = false;

    const check = async () => {
      const health = await fetchHelperHealth();
      if (cancelled) {
        return;
      }

      const connection = resolveHelperConnectionState(health);
      if (connection === 'ready' || connection === 'setup_required') {
        markHelperInstalledOnThisDevice();
      }

      if (connection === 'offline') {
        promptForOfflineHelper();
      } else if (connection === 'setup_required') {
        useArduinoSetupDialogStore.getState().showStartupPromptOnce();
      }
    };

    void check();

    return () => {
      cancelled = true;
    };
  }, []);
}

/** Close the start dialog automatically when the helper comes online. */
export function useHelperStartDialogAutoClose(): void {
  const helperOnline = useSerialStore((state) => state.helperOnline);
  const helperSetupRequired = useSerialStore((state) => state.helperSetupRequired);
  const closeStartDialog = useHelperStartDialogStore((state) => state.close);

  useEffect(() => {
    if (helperOnline || helperSetupRequired) {
      closeStartDialog();
    }
  }, [helperOnline, helperSetupRequired, closeStartDialog]);
}
